import React, { useState } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { createEquipementApi } from "../../../../services/equipementService";
import { fetchEquipmentList } from "../../../../store/equipmentSlice";
import {
  selectActiveProjectId,
  selectActiveProjectName,
} from "../../../../store/projectSlice";

const CONDITION_OPTIONS = ["New", "Good", "Fair", "Poor"];
const TYPE_OPTIONS = [
  "Excavator",
  "Crane",
  "Bulldozer",
  "Loader",
  "Compactor",
  "Generator",
  "Concrete Mixer",
  "Forklift",
  "Other",
];

const SERVICE_MODES = [
  {
    value: "Hour-Based",
    label: "Hour-Based",
    icon: "la-clock-o",
    desc: "Tracks operating hours. Service alerts fire after a set number of hours. Typical for excavators, cranes, generators.",
  },
  {
    value: "Fuel Consumption",
    label: "Fuel-Based",
    icon: "la-tint",
    desc: "Tracks fuel consumed (litres). Service alerts fire when cumulative fuel hits a threshold. Typical for bulldozers, compactors, concrete mixers.",
  },
];

const emptyForm = {
  serviceMode: "",
  eqpName: "",
  eqpType: "",
  eqpNumber: "",
  eqpMake: "",
  eqpModel: "",
  eqpYear: "",
  eqpCondition: "",
};

const AddEquipement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeProjectId = useSelector(selectActiveProjectId);
  const activeProjectName = useSelector(selectActiveProjectName);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.serviceMode) e.serviceMode = "Select a service mode.";
    if (!form.eqpName.trim()) e.eqpName = "Equipment name is required.";
    if (!form.eqpType.trim()) e.eqpType = "Equipment type is required.";
    //   if (form.eqpQuantity === "" || form.eqpQuantity === null)
    //     e.eqpQuantity = "Quantity is required.";
    // else if (Number(form.eqpQuantity) < 0)
    //   e.eqpQuantity = "Quantity must be 0 or more.";
    if (form.eqpYear && !/^\d{4}$/.test(String(form.eqpYear)))
      e.eqpYear = "Enter a valid 4-digit year.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      serviceMode: form.serviceMode,
      eqpName: form.eqpName.trim(),
      eqpType: form.eqpType.trim(),
    };
    if (activeProjectId) payload.projectId = activeProjectId;
    if (form.eqpNumber.trim()) payload.eqpNumber = form.eqpNumber.trim();
    if (form.eqpMake.trim()) payload.eqpMake = form.eqpMake.trim();
    if (form.eqpModel.trim()) payload.eqpModel = form.eqpModel.trim();
    if (form.eqpYear) payload.eqpYear = Number(form.eqpYear);
    if (form.eqpCondition) payload.eqpCondition = form.eqpCondition;

    try {
      setSubmitting(true);
      await createEquipementApi(payload);

      // Refresh the Redux list so List.jsx reflects the new entry immediately
      dispatch(fetchEquipmentList());

      Swal.fire({
        icon: "success",
        title: "Equipment Logged!",
        text: `${payload.eqpName} has been added to the daily report.`,
        confirmButtonText: "View List",
      }).then(() => navigate("/progress/equipement/list"));
    } catch (err) {
      const msg =
        err.response?.data?.message ??
        "Something went wrong. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Add Equipment" motherMenu="Daily Progress" />

      <div className="row">
        <div className="col-xl-12 col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Log Equipment — Shift Report</h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate>
                {/* ── Service Mode — set once at creation, drives all metrics ── */}
                <div className="mb-4">
                  <label className="form-label fw-bold mb-2">
                    Service Mode <span className="text-danger">*</span>
                  </label>
                  <p className="text-muted small mb-2">
                    This determines how service intervals and alerts are tracked for this equipment. Cannot be changed after creation.
                  </p>
                  <div className="row g-3">
                    {SERVICE_MODES.map((mode) => {
                      const selected = form.serviceMode === mode.value;
                      return (
                        <div className="col-md-6" key={mode.value}>
                          <div
                            className={`card h-100 cursor-pointer ${selected ? "border-primary bg-primary-light" : "border"}`}
                            style={{
                              cursor: "pointer",
                              borderWidth: selected ? 2 : 1,
                              transition: "all 0.15s",
                            }}
                            onClick={() => {
                              setForm((prev) => ({ ...prev, serviceMode: mode.value }));
                              setErrors((prev) => ({ ...prev, serviceMode: "" }));
                            }}
                          >
                            <div className="card-body d-flex gap-3 align-items-start">
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${selected ? "bg-primary text-white" : "bg-light text-muted"}`}
                                style={{ width: 44, height: 44 }}
                              >
                                <i className={`la ${mode.icon} fs-4`} />
                              </div>
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <input
                                    type="radio"
                                    name="serviceMode"
                                    value={mode.value}
                                    checked={selected}
                                    onChange={() => {
                                      setForm((prev) => ({ ...prev, serviceMode: mode.value }));
                                      setErrors((prev) => ({ ...prev, serviceMode: "" }));
                                    }}
                                    style={{ accentColor: "#324336" }}
                                  />
                                  <span className={`fw-bold ${selected ? "text-primary" : ""}`}>{mode.label}</span>
                                </div>
                                <p className="text-muted small mb-0">{mode.desc}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {errors.serviceMode && (
                    <div className="text-danger small mt-1">{errors.serviceMode}</div>
                  )}
                </div>

                <hr className="mb-4" />

                <div className="row">
                  {/* Active Project (read-only) */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Project</label>
                    {activeProjectName ? (
                      <div
                        className="form-control d-flex align-items-center gap-2"
                        style={{
                          background: "#3243360d",
                          border: "1px solid #32433640",
                          color: "#324336",
                          fontWeight: 600,
                        }}
                      >
                        <i
                          className="fas fa-building"
                          style={{ fontSize: 14 }}
                        />
                        {activeProjectName}
                      </div>
                    ) : (
                      <div
                        className="alert alert-warning py-2 mb-0"
                        style={{ fontSize: 13 }}
                      >
                        No project selected.{" "}
                        <a href="/dashboard">Select a project</a> first.
                      </div>
                    )}
                  </div>

                  {/* Equipment Name */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Equipment Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${errors.eqpName ? " is-invalid" : ""}`}
                      name="eqpName"
                      value={form.eqpName}
                      onChange={handleChange}
                      placeholder="e.g. JCB 3CX"
                      required
                    />
                    {errors.eqpName && (
                      <div className="invalid-feedback">{errors.eqpName}</div>
                    )}
                  </div>

                  {/* Equipment Type */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Equipment Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-control${errors.eqpType ? " is-invalid" : ""}`}
                      name="eqpType"
                      value={form.eqpType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select type</option>
                      {TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {errors.eqpType && (
                      <div className="invalid-feedback">{errors.eqpType}</div>
                    )}
                  </div>

                  {/* Quantity */}
                  {/* <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Quantity <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      className={`form-control${errors.eqpQuantity ? " is-invalid" : ""}`}
                      name="eqpQuantity"
                      value={form.eqpQuantity}
                      onChange={handleChange}
                      placeholder="e.g. 2"
                      required
                    />
                    {errors.eqpQuantity && (
                      <div className="invalid-feedback">{errors.eqpQuantity}</div>
                    )}
                  </div> */}

                  {/* Equipment Number */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Equipment Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="eqpNumber"
                      value={form.eqpNumber}
                      onChange={handleChange}
                      placeholder="e.g. MH-12-AB-1234"
                    />
                  </div>

                  {/* Make */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Make</label>
                    <input
                      type="text"
                      className="form-control"
                      name="eqpMake"
                      value={form.eqpMake}
                      onChange={handleChange}
                      placeholder="e.g. Caterpillar"
                    />
                  </div>

                  {/* Model */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Model</label>
                    <input
                      type="text"
                      className="form-control"
                      name="eqpModel"
                      value={form.eqpModel}
                      onChange={handleChange}
                      placeholder="e.g. 320D"
                    />
                  </div>

                  {/* Year */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Year</label>
                    <input
                      type="number"
                      className={`form-control${errors.eqpYear ? " is-invalid" : ""}`}
                      name="eqpYear"
                      value={form.eqpYear}
                      onChange={handleChange}
                      placeholder="e.g. 2019"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                    {errors.eqpYear && (
                      <div className="invalid-feedback">{errors.eqpYear}</div>
                    )}
                  </div>

                  {/* Condition */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Condition</label>
                    <select
                      className="form-control"
                      name="eqpCondition"
                      value={form.eqpCondition}
                      onChange={handleChange}
                    >
                      <option value="">Select condition</option>
                      {CONDITION_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? "Saving…" : "Save Equipment Log"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => navigate("/progress/equipement/list")}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEquipement;
